"use client";
import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar";
import Header from "../Header";
import { Search, Eye, Edit2, X, ChevronsLeft, ChevronsRight } from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useLeaves } from "../../context/LeavesContext"; // ✅ context for API
import { useLeaveTypes } from "../../context/LeaveTypeContext";
import { useAuth } from "../../context/AuthContext";
import { useHolidays } from "../../context/HolidayContext";
import { toast } from "react-toastify";

// Shared lavender focus style used across every text/date/time/select input on this page
const focusRing =
  "outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40";

// ✅ <input type="date"> always gives/expects "YYYY-MM-DD".
// Our backend expects/returns "DD-MM-YYYY". These two helpers convert
// between the two so neither side ever sees the wrong format.

// "YYYY-MM-DD" -> "DD-MM-YYYY" (state -> backend payload)
const toBackendDate = (value) => {
  if (!value) return value;
  const parts = String(value).split("-");
  if (parts.length !== 3) return value; // already something else / invalid, leave as-is
  const [year, month, day] = parts;
  if (year.length !== 4) return value; // not YYYY-MM-DD, don't touch it
  return `${day}-${month}-${year}`;
};

// "DD-MM-YYYY" -> "YYYY-MM-DD" (backend response -> <input type="date"> value)
const toInputDate = (value) => {
  if (!value) return value;
  // already ISO-ish (e.g. "2026-08-24" or "2026-08-24T00:00:00Z")
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return String(value).split(/[T ]/)[0];
  }
  const parts = String(value).split("-");
  if (parts.length !== 3) return value;
  const [day, month, year] = parts;
  if (year.length !== 4) return value; // not DD-MM-YYYY, don't touch it
  return `${year}-${month}-${day}`;
};

// ---------------------------------------------------------------------------
// ✅ WEEKEND / HOLIDAY-AWARE LEAVE DAY COUNTING
// ---------------------------------------------------------------------------
const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday, Saturday
};

// Counts leave days between startStr/endStr (inclusive, both "YYYY-MM-DD"),
// skipping weekends and any date present in `holidays`.
const countLeaveDays = (startStr, endStr, holidays = []) => {
  if (!startStr || !endStr) return "";

  const start = new Date(startStr);
  const end = new Date(endStr);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";
  if (end < start) return "";

  const holidaySet = new Set(
    holidays.map((h) => String(h).split(/[T ]/)[0]) // normalize to YYYY-MM-DD
  );

  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const iso = cursor.toISOString().split("T")[0];
    if (!isWeekend(cursor) && !holidaySet.has(iso)) {
      count += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

export default function Leaves() {
  const { leaves, fetchLeaves, updateLeave, addLeave, setLeaves, updateLeaveStatus } = useLeaves(); // ✅ get from context
  const { leaveTypes } = useLeaveTypes(); // ✅ get from context
  const { canDo } = useAuth();
  const { holidays: rawHolidays } = useHolidays(); // ✅ real holiday list from HolidayContext

  // ✅ HolidayContext gives us objects like { id, date, status, ... }, not
  // plain date strings — and countLeaveDays() needs a flat array of
  // "YYYY-MM-DD" strings to match against. This normalizes that:
  //   - drops inactive/disabled holidays so they don't block real leave days
  //   - converts each date to "YYYY-MM-DD" regardless of whether the API
  //     sends it as "DD-MM-YYYY" (same convention as leave dates) or
  //     already-ISO "YYYY-MM-DD"
  //
  // ✅ Resilient to field-name differences: tries the common variants
  // instead of assuming a single key, since a wrong guess here means a
  // holiday silently gets ignored (looks like "it's not excluding it").
  const holidays = (rawHolidays || [])
    .filter((h) => (h.status ? h.status === "active" : true))
    .map((h) => {
      const rawDate =
        h.date ?? h.holiday_date ?? h.holidayDate ?? h.leave_date ?? null;
      if (!rawDate) {
        // Helps you see the real shape once, instead of days silently
        // not being excluded with no clue why.
        console.warn(
          "[Leaves] Couldn't find a date field on this holiday object — " +
            "update the field-name list in Leaves.jsx to match:",
          h
        );
      }
      return toInputDate(rawDate);
    })
    .filter(Boolean);

  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const statusColors = {
    approved: "bg-blue-100 text-blue-600",
    rejected: "bg-red-100 text-red-600",
    pending: "bg-yellow-100 text-yellow-600",
  };

  useEffect(() => {
    fetchLeaves();
  }, []);

  const resetNewLeave = () => {
    setNewLeave({
      leave_type_id: "",
      leave_duration: "",
      description: "",
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      number_of_days: "",
    });
    setErrorMessage("");
  };

  const filteredLeaves = (leaves || []).filter((emp) => {
    // ---------------------------
    // 1. STATUS FILTER
    // ---------------------------
    const matchesStatus =
      filter === "All" ||
      emp.status?.toLowerCase() === filter.toLowerCase();

    // ---------------------------
    // 2. NAME / SEARCH FILTER
    // ---------------------------
    const fullName = `${emp.first_name ?? ""} ${emp.last_name ?? ""}`.trim();
    const searchLower = search.toLowerCase();

    const leaveType = emp.leave_type ?? "";

    const matchesSearch =
      fullName.toLowerCase().includes(searchLower) ||
      leaveType.toLowerCase().includes(searchLower);

    // ---------------------------
    // 3. DATE RANGE FILTER
    // ---------------------------
    // ✅ FIX: this filter sits right above a table of leave From/To dates,
    // so a user picking "03/09/2026 to 08/09/2026" expects to see leaves
    // that fall within/overlap that window — not leaves *submitted* in
    // that window. The previous version filtered on create_audit_time
    // (Submission Date), so if every leave happened to be submitted on
    // the same day (e.g. all created 02/09), picking any range that
    // excluded that single submission day hid every row — even rows
    // whose actual leave period (From/To) was squarely inside the
    // selected range. Filter on the leave's own start_date/end_date
    // instead, using a standard range-overlap check: a leave matches if
    // its period overlaps the selected [from, to] window at all.
    //
    // Normalize both leave dates through toInputDate() first — they may
    // come back as "DD-MM-YYYY", already "YYYY-MM-DD", or a timestamp —
    // so `new Date()` always gets an unambiguous "YYYY-MM-DD" string
    // instead of silently producing an Invalid Date or a misread date.
    const empStartRaw = emp.start_date
      ? toInputDate(String(emp.start_date).split(/[T ]/)[0])
      : null;
    const empEndRaw = emp.end_date
      ? toInputDate(String(emp.end_date).split(/[T ]/)[0])
      : null;

    const empStart = empStartRaw ? new Date(empStartRaw) : null;
    const empEnd = empEndRaw ? new Date(empEndRaw) : null;

    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    if (from) from.setHours(0, 0, 0, 0);
    if (to) to.setHours(23, 59, 59, 999);

    // Range overlap: the leave's start must be on/before the filter's
    // "to", and the leave's end must be on/after the filter's "from".
    // If a leave date is missing/unparseable, don't let it silently
    // exclude the row — fail open instead of hiding real data.
    const startOk =
      !empStart || isNaN(empStart.getTime()) || !to || empStart <= to;
    const endOk =
      !empEnd || isNaN(empEnd.getTime()) || !from || empEnd >= from;

    const matchesDate = startOk && endOk;

    // ---------------------------
    // FINAL COMBINE
    // ---------------------------
    return matchesStatus && matchesSearch && matchesDate;
  });

  const totalPages = Math.ceil(filteredLeaves.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredLeaves.slice(indexOfFirstRow, indexOfLastRow);

  // ✅ dynamic column count so colSpan is always correct, regardless of
  // which optional columns (Description / Actions) are visible
  const canShowDescriptionCol = canDo("Leaves", "Edit");
  const canShowActionsCol =
    canDo("Leaves", "Edit") || canDo("Leaves", "Delete") || canDo("Leaves", "View");
  const totalColumns =
    8 + (canShowDescriptionCol ? 1 : 0) + (canShowActionsCol ? 1 : 0);
  // base 8 = ID, Submission Date, Employee Name, Leave Type, From, To, Days, Status

  // ✅ Edit is only allowed while the leave is still pending.
  // Once it has been approved or rejected, the Edit icon is hidden
  // (View remains available so the record can still be reviewed).
  const canEditLeave = (emp) =>
    canDo("Leaves", "Edit") && emp.status?.toLowerCase() === "pending";

  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newLeave.leave_duration === "half_day") {
      if (newLeave.start_date !== newLeave.end_date) {
        setErrorMessage("Half-day leave must have the same start and end date.");
        return;
      }
    }

    // ✅ 1. VALIDATION FIRST (before loading)
    if (
      !newLeave.leave_type_id ||
      !newLeave.leave_duration ||
      !newLeave.description ||
      !newLeave.start_date ||
      !newLeave.end_date ||
      !newLeave.number_of_days
    ) {
      setErrorMessage("All fields are required!");
      return;
    }

    setErrorMessage(""); // clear old error
    setLoading(true); // ✅ only start loading after validation passes

    try {
      const res = await addLeave({
        ...newLeave,
        // ✅ convert from the <input type="date"> format to what the backend expects
        start_date: toBackendDate(newLeave.start_date),
        end_date: toBackendDate(newLeave.end_date),
      });

      if (res.success) {
        setShowAddModal(false);
        resetNewLeave();
      } else {
        const msg = res.message || "Something went wrong";
        // ✅ Backend saves the leave first, then sends a notification email;
        // if only that email step fails, it still reports an error even
        // though the leave record already exists. Don't block the user with
        // a false failure here — refresh the list so the new leave shows up
        // and surface it as a warning instead of a stuck form error.
        if (/mail|email/i.test(msg)) {
          setShowAddModal(false);
          resetNewLeave();
          await fetchLeaves();
          toast.warn(msg);
        } else {
          setErrorMessage(msg);
        }
      }
    } catch (err) {
      setErrorMessage("Server error occurred");
    } finally {
      setLoading(false); // ✅ always stop loading
    }
  };

  const handleEdit = (emp) => {
    // Guard: even if the Edit icon somehow gets triggered on a
    // non-pending leave, don't open edit mode for it.
    if (emp.status?.toLowerCase() !== "pending") {
      return;
    }

    // ✅ FIX: the leaves list endpoint (used to populate the table) returns
    // a joined "leave_type" NAME but may not include the "leave_type_id"
    // foreign key on every row. If we blindly spread `emp` and it's missing
    // leave_type_id, the edit modal's <select value={selected.leave_type_id}>
    // won't match any <option>, so it silently falls back to "Select Leave
    // Type" (blank) even though a real type is already assigned. If the user
    // then doesn't touch the dropdown, we send an empty/undefined
    // leave_type_id on save and the update appears to "not stick".
    //
    // Resolve it defensively: prefer emp.leave_type_id if present, otherwise
    // look it up from the leaveTypes list by matching the name we do have.
    const resolvedLeaveTypeId =
      emp.leave_type_id ??
      leaveTypes.find((lt) => lt.name === emp.leave_type)?.id ??
      "";

    setSelected({
      ...emp,
      leave_type_id: resolvedLeaveTypeId,
      // ✅ convert dates coming back from the backend (DD-MM-YYYY)
      // into the format <input type="date"> needs (YYYY-MM-DD),
      // otherwise the date pickers will render blank.
      start_date: toInputDate(emp.start_date),
      end_date: toInputDate(emp.end_date),
      start_time: emp.start_time || "",
      end_time: emp.end_time || "",
    });
    setIsEditMode(true);
  };

  const handleView = (emp) => {
    setSelected(emp);
    setIsEditMode(false);
  };

  // Add state for Add Modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Handler to save new leave
  const handleAddLeave = () => {
    const nextId = Math.max(...leaves.map(l => l.id)) + 1;
    setLeaves(prev => [...prev, { ...newLeave, id: nextId }]);
    setIsAddModalOpen(false);
    setNewLeave({
      id: "",
      name: "",
      leave_type_id: "",
      start_date: "",
      end_date: "",
      number_of_days: 0,
      status: "Pending",
      avatar: "https://i.pravatar.cc/40"
    });
  };
  const [showAddModal, setShowAddModal] = useState(false);
  const [newLeave, setNewLeave] = useState({
    leave_type_id: "",
    leave_duration: "",
    description: "",
    start_date: "",
    end_date: "",
    start_time: "",
    end_time: "",
    number_of_days: "",
  });

  const handleSave = async () => {
    if (!selected?.id) {
      alert("Missing leave ID — cannot save.");
      return;
    }

    if (!selected.leave_type_id) {
      setErrorMessage("Please select a leave type.");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user"));

    const updateData = {
      // ✅ FIX: <select> values are always strings ("3"), while leave_type_id
      // usually comes from the API/DB as a number. If the backend does
      // strict type checking on the update payload, a stringified id can be
      // silently ignored/rejected instead of throwing a visible error.
      // Coerce to a number before sending.
      leave_type_id: Number(selected.leave_type_id),
      // ✅ convert from the <input type="date"> format (YYYY-MM-DD)
      // to what the backend expects (DD-MM-YYYY)
      start_date: toBackendDate(selected.start_date),
      end_date: toBackendDate(selected.end_date),
      start_time: selected.start_time,
      end_time: selected.end_time,
      user_id: selected.user_id,
      status: selected.status,
      leave_duration: selected.leave_duration,
      description: selected.description,
      number_of_days: selected.number_of_days,
      update_audit_id: user?.id,
    };

    const result = await updateLeave(selected.id, updateData);

    if (result.success) {
      setSelected(null);
      fetchLeaves();
    } else {
      // keep modal open on failure so the user can retry / see what's wrong,
      // instead of silently closing it
      alert(result.message || "Failed to update leave");
    }
  };

  const handleDateChange = (field, value) => {
    const updatedLeave = { ...newLeave, [field]: value };

    const start = updatedLeave.start_date ? new Date(updatedLeave.start_date) : null;
    const end = updatedLeave.end_date ? new Date(updatedLeave.end_date) : null;

    // ❌ HALF DAY VALIDATION (REAL TIME)
    if (
      updatedLeave.leave_duration === "half_day" &&
      updatedLeave.start_date &&
      updatedLeave.end_date &&
      updatedLeave.start_date !== updatedLeave.end_date
    ) {
      setErrorMessage("Half-day leave must have SAME start and end date");
    } else {
      setErrorMessage(""); // clear error if valid
    }

    if (start && end && end >= start) {
      if (updatedLeave.leave_duration === "half_day") {
        updatedLeave.number_of_days = 0.5;
      } else {
        // ✅ exclude weekends + holidays from the count instead of a raw
        // calendar-day diff
        updatedLeave.number_of_days = countLeaveDays(
          updatedLeave.start_date,
          updatedLeave.end_date,
          holidays
        );
      }
    } else {
      updatedLeave.number_of_days = "";
    }

    setNewLeave(updatedLeave);
  };

  // Show only the date portion (no time) for submission date, regardless of
  // whether the backend sends "2024-05-01T10:23:00Z", "2024-05-01 10:23:00",
  // "DD-MM-YYYY", or just "2024-05-01"
  const formatDateOnly = (value) => {
    if (!value) return "";
    // ✅ normalize through the same DD-MM-YYYY <-> YYYY-MM-DD helper used
    // everywhere else, instead of assuming the raw string is already ISO.
    const isoDate = toInputDate(String(value).split(/[T ]/)[0]);
    const parsed = new Date(isoDate);
    if (isNaN(parsed.getTime())) return String(value).split(/[T ]/)[0];
    return parsed.toLocaleDateString();
  };

  // ✅ used by the Edit modal's date pickers — now weekend/holiday aware
  const recalcDays = (start, end, duration, holidayList = holidays) => {
    if (!start || !end) return "";
    if (duration === "half_day") return 0.5;
    return countLeaveDays(start, end, holidayList);
  };

  // ✅ the backend can return number_of_days as a padded decimal string
  // (e.g. "0.50000" for a half-day leave), which rendered as-is looks like
  // a run of zeros in the table. Normalize to a plain number for display.
  const formatDays = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const num = Number(value);
    return Number.isNaN(num) ? value : num;
  };

  // small helper for the "view" mode read-only rows in the details modal
  const ReadOnlyField = ({ label, value }) => (
    <div>
      <label className="text-sm font-medium text-gray-600">{label}</label>
      <div className="w-full border rounded-md px-3 py-2 mt-1 bg-gray-50 text-gray-700 break-words">
        {value || value === 0 ? value : "—"}
      </div>
    </div>
  );

  return (
    <div className="bg-sky-50">
      <div className="px-4 sm:px-6 md:px-9 py-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">
          Leaves
        </h1>

        {/* Filter + Search */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 bg-white rounded-xl p-3 shadow-sm gap-3">

          {/* LEFT */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full lg:w-auto">

            {/* Filter + Search */}
            <div className="flex flex-col xs:flex-row sm:flex-row items-stretch border rounded-md overflow-hidden w-full sm:w-auto transition-all focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">

              {/* STATUS FILTER */}
              <select
                className="px-2 py-2 border-b sm:border-b-0 sm:border-r text-sm w-full sm:w-auto outline-none"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="All">All</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>

              {/* SEARCH */}
              <div className="relative w-full sm:w-40 md:w-56">
                <Search className="absolute left-2 top-2.5 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="pl-8 pr-2 py-2 w-full outline-none text-sm"
                />
              </div>

            </div>

            {/* DATE FILTER */}
            <div className="flex items-center gap-2 border rounded-md px-2 py-2 w-full sm:w-auto transition-all focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
              <input
                type="date"
                className="text-sm w-full sm:w-auto min-w-0 outline-none"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <span className="text-gray-400 text-sm shrink-0">to</span>
              <input
                type="date"
                className="text-sm w-full sm:w-auto min-w-0 outline-none"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {(search || filter !== "All" || fromDate || toDate) && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setFilter("All");
                  setFromDate("");
                  setToDate("");
                  setCurrentPage(1);
                }}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0"
              >
                <X size={14} />
                Clear Filters
              </button>
            )}

          </div>

          {/* RIGHT BUTTON */}
          <button
            onClick={() => setShowAddModal(true)}
            className="text-white px-4 py-2 rounded-md shrink-0 w-full lg:w-auto"
            style={{
              background: "linear-gradient(135deg, #2563eb, #d12465)"
            }}
          >
            + Add Leave
          </button>

        </div>

        {/* Table */}
        <div className="bg-white shadow rounded-xl p-3 sm:p-4">
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-[900px] sm:min-w-full border-collapse text-sm sm:text-base">
              <thead className="bg-sky-50">
                <tr>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                    ID
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Submission Date
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Employee Name
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Leave Type
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                    From
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                    To
                  </th>
                  {canShowDescriptionCol && (
                    <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                      Description
                    </th>
                  )}
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Days
                  </th>
                  <th className="py-3 px-2 sm:px-4 text-left font-semibold text-gray-600 whitespace-nowrap">
                    Status
                  </th>
                  {canShowActionsCol && (
                    <th className="py-3 px-2 sm:px-4 text-center font-semibold text-gray-600 whitespace-nowrap">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={totalColumns} className="text-center py-4 text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : currentRows.length > 0 ? (
                  currentRows.map((emp, index) => (
                    <tr
                      key={emp.id}
                      className={`border-b hover:bg-gray-50 ${
                        index % 2 === 1 ? "bg-[#f7f6fe]" : "bg-white"
                      }`}
                    >
                      <td className="py-3 px-2 sm:px-4 text-[#8c57ff] whitespace-nowrap">
                        {(index + 1).toString().padStart(2, "0")}
                      </td>
                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                        {formatDateOnly(emp.create_audit_time)}
                      </td>
                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                        {emp.first_name} {emp.last_name}
                      </td>
                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                        {emp.leave_type}
                      </td>
                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                        {emp.start_date}
                        {emp.leave_duration === "half_day" && emp.start_time
                          ? ` ${emp.start_time}`
                          : ""}
                      </td>

                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                        {emp.end_date}
                        {emp.leave_duration === "half_day" && emp.end_time
                          ? ` ${emp.end_time}`
                          : ""}
                      </td>

                      {canShowDescriptionCol && (
                        <td className="py-3 px-2 sm:px-4 max-w-[160px]">
                          <span
                            className="block truncate"
                            title={emp.description || ""}
                          >
                            {emp.description || "—"}
                          </span>
                        </td>
                      )}

                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                        {formatDays(emp.number_of_days)}
                      </td>

                      <td className="py-3 px-2 sm:px-4 whitespace-nowrap">
                        {emp.status === "pending" ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateLeaveStatus(emp.id, "approved")}
                              className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600 hover:bg-green-200"
                            >
                              Approve
                            </button>

                            <button
                              onClick={() => updateLeaveStatus(emp.id, "rejected")}
                              className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600 hover:bg-red-200"
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              statusColors[emp.status] || "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {emp.status}
                          </span>
                        )}
                      </td>

                      {canShowActionsCol && (
                        <td className="py-3 px-2 sm:px-4">
                          <div className="flex justify-center items-center space-x-2 text-gray-600">
                            {/* ✅ Edit is only shown while the leave is pending.
                                Once approved/rejected, only View remains. */}
                            {canEditLeave(emp) && (
                              <Edit2
                                className="h-4 w-4 cursor-pointer hover:text-sky-600"
                                onClick={() => handleEdit(emp)}
                              />
                            )}
                            {canDo("Leaves", "View") && (
                              <Eye
                                className="h-4 w-4 cursor-pointer hover:text-sky-600"
                                onClick={() => handleView(emp)}
                              />
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={totalColumns} className="text-center py-4 text-gray-500">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3 text-sm">
            <div className="text-gray-500">
              Rows per page:
              <select
                className="border rounded-md px-2 py-1 ml-1 outline-none transition-all focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff]/40"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[5, 10, 15].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center flex-wrap justify-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsLeft size={20} />
              </button>
              <button
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <MdKeyboardArrowLeft size={20} />
              </button>

              {(() => {
                let startPage = Math.max(currentPage - 1, 1);
                let endPage = startPage + 2;
                if (endPage > totalPages) {
                  endPage = totalPages;
                  startPage = Math.max(endPage - 2, 1);
                }
                const pages = [];
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        i === currentPage
                          ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                      onClick={() => setCurrentPage(i)}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}

              <button
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                <MdKeyboardArrowRight size={20} />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronsRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Leave Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3 sm:px-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl p-4 sm:p-6 relative max-h-[92vh] overflow-y-auto">

            {/* CLOSE */}
            <button
              onClick={() => setShowAddModal(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="text-xl font-semibold mb-4 pr-6">
              Apply For A Leave
            </h2>

            <div className="space-y-3">

              {/* LEAVE TYPE + DURATION */}
              <div className="flex flex-col sm:flex-row gap-2 w-full">

                {/* Leave Type */}
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-600">
                    Leave Type
                  </label>

                  <select
                    value={newLeave.leave_type_id}
                    onChange={(e) =>
                      setNewLeave({
                        ...newLeave,
                        leave_type_id: e.target.value
                      })
                    }
                    className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                    disabled={loading}
                  >
                    <option value="">-- Select Type of Leave --</option>
                    {leaveTypes
                      .filter((lt) => lt.status === "active")
                      .map((leaveType) => (
                        <option key={leaveType.id} value={leaveType.id}>
                          {leaveType.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Duration */}
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-600">
                    Duration
                  </label>

                  <select
                    value={newLeave.leave_duration}
                    onChange={(e) => {
                      const value = e.target.value;

                      let updated = {
                        ...newLeave,
                        leave_duration: value
                      };

                      // CASE 1: HALF DAY
                      if (value === "half_day") {
                        updated.end_date = updated.start_date;
                        updated.number_of_days = 0.5;
                      }

                      // CASE 2: FULL DAY → RECALCULATE PROPERLY
                      // ✅ excludes weekends + holidays from the count
                      if (value === "full_day") {
                        if (updated.start_date && updated.end_date) {
                          updated.number_of_days = countLeaveDays(
                            updated.start_date,
                            updated.end_date,
                            holidays
                          );
                        } else {
                          updated.number_of_days = "";
                        }
                      }

                      setNewLeave(updated);
                    }}
                    className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                  >
                    <option value="">Select</option>
                    <option value="half_day">Half Day</option>
                    <option value="full_day">Full Day</option>
                  </select>
                </div>

              </div>

              {/* DESCRIPTION */}
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Description
                </label>
                <textarea
                  rows="5"
                  value={newLeave.description}
                  onChange={(e) =>
                    setNewLeave({
                      ...newLeave,
                      description: e.target.value
                    })
                  }
                  className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                />
              </div>

              {/* =========================
                  FULL DAY / DEFAULT UI
              ========================== */}
              {(!newLeave.leave_duration ||
                newLeave.leave_duration === "full_day") && (
                <div className="flex flex-col sm:flex-row gap-2">

                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-600">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                      value={newLeave.start_date}
                      onChange={(e) => {
                        const value = e.target.value;

                        const updated = {
                          ...newLeave,
                          start_date: value
                        };

                        // force sync for half day
                        if (newLeave.leave_duration === "half_day") {
                          updated.end_date = value;
                        }

                        setNewLeave(updated);
                      }}
                    />
                  </div>

                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-600">
                      End Date
                    </label>
                    <input
                      type="date"
                      className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                      value={newLeave.end_date}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (newLeave.leave_duration === "half_day") {
                          setErrorMessage("Half-day leave must be same date");
                          setNewLeave({
                            ...newLeave,
                            end_date: newLeave.start_date
                          });
                          return;
                        }

                        setErrorMessage("");
                        handleDateChange("end_date", value);
                      }}
                      min={newLeave.start_date || ""}
                    />
                  </div>

                </div>
              )}

              {/* =========================
                  HALF DAY UI (DATES + TIME)
              ========================== */}
              {newLeave.leave_duration === "half_day" && (
                <>
                  {/* Dates (same layout as full day) */}
                  <div className="flex flex-col sm:flex-row gap-2">

                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-600">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                        value={newLeave.start_date}
                        onChange={(e) => {
                          const value = e.target.value;

                          const updated = {
                            ...newLeave,
                            start_date: value
                          };

                          // force sync for half day
                          if (newLeave.leave_duration === "half_day") {
                            updated.end_date = value;
                          }

                          setNewLeave(updated);
                        }}
                      />
                    </div>

                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-600">
                        End Date
                      </label>
                      <input
                        type="date"
                        className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                        value={newLeave.end_date}
                        onChange={(e) => {
                          const value = e.target.value;

                          if (newLeave.leave_duration === "half_day") {
                            setErrorMessage("Half-day leave must be same date");
                            setNewLeave({
                              ...newLeave,
                              end_date: newLeave.start_date
                            });
                            return;
                          }

                          setErrorMessage("");
                          handleDateChange("end_date", value);
                        }}
                        min={newLeave.start_date || ""}
                      />
                    </div>

                  </div>

                  {/* TIME FIELDS */}
                  <div className="flex flex-col sm:flex-row gap-2 mt-2">

                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-600">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={newLeave.start_time || ""}
                        onChange={(e) =>
                          setNewLeave({
                            ...newLeave,
                            start_time: e.target.value
                          })
                        }
                        className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                      />
                    </div>

                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-600">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={newLeave.end_time || ""}
                        onChange={(e) =>
                          setNewLeave({
                            ...newLeave,
                            end_time: e.target.value
                          })
                        }
                        className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                      />
                    </div>

                  </div>
                </>
              )}

              {/* NUMBER OF DAYS */}
              <div>
                <label className="text-sm font-medium text-gray-600">
                  Number of Days
                </label>
                <input
                  type="number"
                  value={newLeave.number_of_days || ""}
                  readOnly
                  className="w-full border rounded-md px-3 py-2 mt-1 bg-gray-100 cursor-not-allowed"
                />
                {/* ✅ small hint so it's clear this excludes weekends/holidays */}
                <p className="text-xs text-gray-400 mt-1">
                  Weekends and holidays are excluded automatically.
                </p>
              </div>

              {/* ERROR */}
              {errorMessage && (
                <div className="text-red-500 text-sm">
                  {errorMessage}
                </div>
              )}

              {/* ACTIONS */}
              <div className="mt-5 flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-2">

                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetNewLeave();
                  }}
                  className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 rounded-md text-white flex items-center justify-center"
                  disabled={loading}
                  style={{
                    background:
                      "linear-gradient(135deg, #2563eb, #d12465)"
                  }}
                >
                  {loading ? "Applying..." : "Apply"}
                </button>

              </div>

            </div>
          </div>
        </div>
      )}

      {/* View / Edit Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-3 sm:px-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md relative max-h-[92vh] flex flex-col">

            {/* FIXED HEADER */}
            <div className="flex items-center justify-between px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b shrink-0">
              <h2 className="text-lg sm:text-xl font-semibold pr-6">
                {isEditMode ? "Edit Leave Details" : "View Leave Details"}
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-500 hover:text-gray-800"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* SCROLLABLE BODY */}
            <div className="leave-modal-scroll overflow-y-auto px-4 sm:px-5 py-4 space-y-3">

              {/* LEAVE TYPE */}
              {isEditMode ? (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Leave Type
                  </label>
                  <select
                    className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                    value={selected.leave_type_id || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelected((prev) => ({
                        ...prev,
                        leave_type_id: value,
                        leave_type: leaveTypes.find(l => l.id == value)?.name || ""
                      }));
                    }}
                  >
                    <option value="">Select Leave Type</option>
                    {leaveTypes
                      .filter((lt) => lt.status === "active")
                      .map((lt) => (
                        <option key={lt.id} value={lt.id}>
                          {lt.name}
                        </option>
                      ))}
                  </select>
                </div>
              ) : (
                <ReadOnlyField label="Leave Type" value={selected.leave_type} />
              )}

              {/* DURATION */}
              {isEditMode ? (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Duration
                  </label>
                  <select
                    className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                    value={selected.leave_duration || ""}
                    onChange={(e) => {
                      const leave_duration = e.target.value;
                      setSelected((prev) => {
                        const updated = { ...prev, leave_duration };
                        // ✅ recalculate days when duration type changes too
                        updated.number_of_days = recalcDays(
                          prev.start_date,
                          prev.end_date,
                          leave_duration
                        );
                        return updated;
                      });
                    }}
                  >
                    <option value="">Select</option>
                    <option value="half_day">Half Day</option>
                    <option value="full_day">Full Day</option>
                  </select>
                </div>
              ) : (
                <ReadOnlyField
                  label="Duration"
                  value={
                    selected.leave_duration === "half_day"
                      ? "Half Day"
                      : selected.leave_duration === "full_day"
                      ? "Full Day"
                      : ""
                  }
                />
              )}

              {/* EMPLOYEE NAME */}
              {isEditMode ? (
                <div>
                  <label className="text-sm font-medium text-gray-600">Employee Name</label>
                  <select
                    className={`w-full border rounded-md px-3 py-2 mt-1 bg-white ${focusRing}`}
                    value={selected.first_name || ""}
                    onChange={(e) => {
                      const selectedEmp = leaves.find(
                        (emp) => emp.first_name === e.target.value
                      );
                      if (selectedEmp) {
                        setSelected({
                          ...selected,
                          first_name: selectedEmp.first_name,
                          last_name: selectedEmp.last_name,
                        });
                      }
                    }}
                  >
                    <option value="">Select Employee</option>
                    {leaves.map((emp) => (
                      <option key={emp.id} value={emp.first_name}>
                        {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <ReadOnlyField
                  label="Employee Name"
                  value={
                    selected.first_name && selected.last_name
                      ? `${selected.first_name} ${selected.last_name}`
                      : ""
                  }
                />
              )}

              {/* DATES */}
              {isEditMode ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-600">
                      From
                    </label>
                    <input
                      type="date"
                      className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                      value={selected.start_date}
                      onChange={(e) => {
                        const start_date = e.target.value;

                        setSelected((prev) => {
                          const updated = {
                            ...prev,
                            start_date,
                          };

                          // ✅ excludes weekends + holidays from the count
                          updated.number_of_days = recalcDays(
                            start_date,
                            prev.end_date,
                            prev.leave_duration
                          );

                          return updated;
                        });
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-600">To</label>
                    <input
                      type="date"
                      className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                      value={selected.end_date}
                      onChange={(e) => {
                        const end_date = e.target.value;

                        setSelected((prev) => {
                          const updated = {
                            ...prev,
                            end_date,
                          };

                          // ✅ excludes weekends + holidays from the count
                          updated.number_of_days = recalcDays(
                            prev.start_date,
                            end_date,
                            prev.leave_duration
                          );

                          return updated;
                        });
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1">
                    <ReadOnlyField label="From" value={selected.start_date} />
                  </div>
                  <div className="flex-1">
                    <ReadOnlyField label="To" value={selected.end_date} />
                  </div>
                </div>
              )}

              {/* TIME (half day only) */}
              {selected.leave_duration === "half_day" && (
                isEditMode ? (
                  <div className="flex flex-col sm:flex-row gap-2 mt-2">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-600">
                        Start Time
                      </label>
                      <input
                        type="time"
                        className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                        value={selected.start_time || ""}
                        onChange={(e) =>
                          setSelected((prev) => ({
                            ...prev,
                            start_time: e.target.value
                          }))
                        }
                      />
                    </div>

                    <div className="flex-1">
                      <label className="text-sm font-medium text-gray-600">
                        End Time
                      </label>
                      <input
                        type="time"
                        className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                        value={selected.end_time || ""}
                        onChange={(e) =>
                          setSelected((prev) => ({
                            ...prev,
                            end_time: e.target.value
                          }))
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1">
                      <ReadOnlyField label="Start Time" value={selected.start_time} />
                    </div>
                    <div className="flex-1">
                      <ReadOnlyField label="End Time" value={selected.end_time} />
                    </div>
                  </div>
                )
              )}

              {/* DESCRIPTION — editable in edit mode, read-only in view mode */}
              {isEditMode ? (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Description
                  </label>
                  <textarea
                    rows="4"
                    className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                    value={selected.description || ""}
                    onChange={(e) =>
                      setSelected((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Description
                  </label>
                  <div className="w-full border rounded-md px-3 py-2 mt-1 bg-gray-50 text-gray-700 whitespace-pre-wrap break-words">
                    {selected.description || "No description available"}
                  </div>
                </div>
              )}

              {/* STATUS */}
              {isEditMode ? (
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <select
                    className={`w-full border rounded-md px-3 py-2 mt-1 ${focusRing}`}
                    value={selected.status || ""}
                    onChange={(e) =>
                      setSelected({ ...selected, status: e.target.value })
                    }
                  >
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              ) : (
                <div>
                  <label className="text-sm font-medium text-gray-600">Status</label>
                  <div className="mt-1">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        statusColors[selected.status] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {selected.status || "—"}
                    </span>
                  </div>
                </div>
              )}

            </div>
            {/* end scrollable body */}

            {/* FIXED FOOTER */}
            {isEditMode && (
              <div className="px-4 sm:px-5 py-3 border-t shrink-0 flex justify-end">
                <button
                  onClick={handleSave}
                  className="text-white px-4 py-2 rounded-md w-full sm:w-auto"
                  style={{
                    background: "linear-gradient(135deg, #2563eb, #d12465)"
                  }}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* thin custom scrollbar for the leave details modal body */}
      <style>{`
        .leave-modal-scroll {
          scrollbar-width: thin;
          scrollbar-color: #c7c7d1 transparent;
        }
        .leave-modal-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .leave-modal-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .leave-modal-scroll::-webkit-scrollbar-thumb {
          background-color: #c7c7d1;
          border-radius: 9999px;
        }
        .leave-modal-scroll::-webkit-scrollbar-thumb:hover {
          background-color: #a3a3af;
        }
      `}</style>
    </div>
  );
}